<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3">
      <SimplePageHeader title="Teams" class="px-4 pr-5 mb-5">
        <template v-slot:right-buttons>
          <ButtonCustomIcon
            icon="add"
            color="primary"
            text="Add"
            :block="true"
            @click="openTeamAddModalHandler"
          />
        </template>
      </SimplePageHeader>
      <v-row
        align="center"
        justify="start"
        class="px-5 mb-sm-5"
      >
        <v-col cols="12" sm="6" md="4" lg="3" xl="2">
          <v-autocomplete
            v-model="teamNameFilter"
            :items="teamNames"
            item-text="teamName"
            label="Team Name"
            outlined
            clearable
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="3" xl="2">
          <UsersDropDownList
            :userId="managerIdFilter"
            label="Team Leader"
            @userIdChange="handleManagerIdChange"
            :clearable="true"
            :securityRoles="[MANAGER]"
          ></UsersDropDownList>
        </v-col>
      </v-row>
      <v-row justify="end">
        <v-col cols="12" sm="6">
          <FilterButtons
            @search="handleSearchClick"
            @clear="handleClearClick"
          ></FilterButtons>
        </v-col>
      </v-row>
      <FilterButtonsSmall
        @search="handleSearchClick"
        @clear="handleClearClick"
      ></FilterButtonsSmall>
    </div>
    <v-container fluid class="px-0 pt-2 mt-4">
      <v-layout column>
        <v-data-table
          dense
          light
          :headers="headers"
          :items="teamsList"
          class="elevation-2"
        >
          <template v-slot:item.action="{ item }">
            <v-btn class="mx-1 my-1"
                   color="primary"
                   outlined
                   @click="showEditTeamModal(item)"
            >
              <v-icon color="primary">edit</v-icon>
            </v-btn>
            <v-btn class="mx-1 my-1"
                   color="red"
                   outlined
                   @click="showDeleteTeamConfirmationDialog(item)"
            >
              <v-icon color="red">delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-layout>
    </v-container>
    <TeamAddModal
      ref="TeamAddModal"
      @saveTeam="getTeamWithFilters"
    ></TeamAddModal>
    <TeamEditModal
      ref="TeamEditModal"
      :teamId="teamIdToEdit"
      :teamName="teamNameToEdit"
      :managerId="managerIdToEdit"
      :commissionBaseAmount="commissionBaseAmountToEdit"
      :commissionPercentage="commissionPercentageToEdit"
      :teamMembers="teamMembersToEdit"
      @teamNameChange="teamNameChangeHandler"
      @managerIdChange="managerIdChangeHandler"
      @commissionBaseAmountChange="commissionBaseAmountChangeHandler"
      @commissionPercentageChange="commissionPercentageChangeHandler"
      @clearTeam="clearTeamHandler"
      @editTeam="editTeamHandler"
      @addTeamMember="addTeamMemberHandler"
      @memberIdChange="memberIdChangeHandler"
      @roleChange="roleChangeHandler"
      @removeMember="removeMemberHandler"
    ></TeamEditModal>
    <ConfirmationModal
      :showModal="showDeleteTeamConfirmationDialogProp"
      :title="'Delete Confirmation'"
      :contentText="'Are you sure you want to delete the selected team?'"
      :toolbarColor="'primary'"
      @cancel-click="closeDeleteTeamConfirmationDialogHandler"
      @ok-click="deleteTeamHandler"
    ></ConfirmationModal>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { SimplePageHeader } from '@/components/pageComponents'
import { BottomSpacer, ButtonCustomIcon, FilterButtons, FilterButtonsSmall } from '@/components/buttons'
import { UsersDropDownList } from '@/components/users'
import { mapFields } from '@/vueHelpers/vuex'
import { mapActions } from 'vuex'
import TeamAddModal from '@/components/teams/TeamAddModal'
import TeamEditModal from '@/components/teams/TeamEditModal'
import { ConfirmationModal } from '@/components/dialogs'
import { MANAGER } from '@/lib/constants/securityRoles'

export default {
  components: {
    TeamAddModal,
    TeamEditModal,
    SimplePageHeader,
    BottomSpacer,
    ButtonCustomIcon,
    FilterButtons,
    FilterButtonsSmall,
    UsersDropDownList,
    ConfirmationModal
  },
  data () {
    return {
      managerIdFilter: null,
      teamNameFilter: null,
      headers: [
        { text: 'Team Name', value: 'teamName', width: '35%' },
        { text: 'Team Leader', value: 'managerName', width: '35%' },
        { text: 'Actions', value: 'action', align: 'center', width: '30%', sortable: false }
      ],
      teamIdToEdit: null,
      teamNameToEdit: null,
      commissionBaseAmountToEdit: null,
      commissionPercentageToEdit: null,
      managerIdToEdit: null,
      teamMembersToEdit: [],
      showDeleteTeamConfirmationDialogProp: false,
      teamIdToDelete: null,
      MANAGER,
      teamNames: []
    }
  },
  computed: {
    ...mapFields('teams', ['teamsList'])
  },
  methods: {
    ...mapActions('teams', ['getTeams', 'deleteTeam']),
    handleManagerIdChange (newValue) {
      this.managerIdFilter = newValue
    },
    handleSearchClick () {
      this.getTeamWithFilters()
    },
    handleClearClick () {
      this.teamNameFilter = null
      this.managerIdFilter = null
      this.getTeamWithFilters()
    },
    openTeamAddModalHandler () {
      this.$refs.TeamAddModal.showModal()
    },
    showEditTeamModal (item) {
      this.teamIdToEdit = item.id
      this.teamNameToEdit = item.teamName
      this.managerIdToEdit = item.managerId
      this.commissionBaseAmountToEdit = item.commissionBaseAmount
      this.commissionPercentageToEdit = item.commissionPercentage
      this.teamMembersToEdit = item.teamMembers
      this.$refs.TeamEditModal.showModal()
    },
    teamNameChangeHandler (newValue) {
      this.teamNameToEdit = newValue
    },
    managerIdChangeHandler (newValue) {
      this.managerIdToEdit = newValue
    },
    commissionBaseAmountChangeHandler (newValue) {
      this.commissionBaseAmountToEdit = newValue
    },
    commissionPercentageChangeHandler (newValue) {
      this.commissionPercentageToEdit = newValue
    },
    clearTeamHandler () {
      this.teamIdToEdit = null
      this.teamNameToEdit = null
      this.managerIdToEdit = null
      this.commissionBaseAmountToEdit = null
      this.commissionPercentageToEdit = null
      this.teamMembersToEdit = []
      this.getTeamWithFilters()
    },
    addTeamMemberHandler () {
      this.teamMembersToEdit.push({ memberId: null, role: null })
    },
    memberIdChangeHandler ({ id, memberId }) {
      this.teamMembersToEdit[id]['memberId'] = memberId
    },
    roleChangeHandler ({ id, role }) {
      this.teamMembersToEdit[id]['role'] = role
    },
    removeMemberHandler (id) {
      this.teamMembersToEdit.splice(id, 1)
    },
    editTeamHandler () {
      this.getTeamWithFilters()
    },
    showDeleteTeamConfirmationDialog (item) {
      this.teamIdToDelete = item.id
      this.showDeleteTeamConfirmationDialogProp = true
    },
    closeDeleteTeamConfirmationDialogHandler () {
      this.teamIdToDelete = null
      this.showDeleteTeamConfirmationDialogProp = false
    },
    async deleteTeamHandler () {
      await this.deleteTeam(this.teamIdToDelete)
      this.showDeleteTeamConfirmationDialogProp = false
      this.getTeamWithFilters()
    },
    getTeamWithFilters () {
      return this.getTeams({ teamName: this.teamNameFilter, managerId: this.managerIdFilter })
    }
  },
  created () {
    this.getTeamWithFilters().then((teams) => {
      this.teamNames = teams.map((team) => { return { teamName: team.teamName } })
      this.teamNames.sort((a, b) => (a.teamName > b.teamName) ? 1 : -1)
    })
  }
}
</script>
